import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';

const NebulaField = ({ audioData, vibeConfig }) => {
  const nebulaRef = useRef();
  
  const nebulaCount = 50;

  // Create nebula particles
  const nebula = useMemo(() => {
    const positions = [];
    const nebulaColors = [];
    const sizes = [];
    
    for (let i = 0; i < nebulaCount; i++) {
      // Spread particles in a large sphere
      const radius = 15 + Math.random() * 25;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      
      positions.push(
        radius * Math.sin(phi) * Math.cos(theta),
        radius * Math.sin(phi) * Math.sin(theta),
        radius * Math.cos(phi)
      );
      
      // Cosmic colors
      const colorChoice = Math.random();
      if (colorChoice < 0.3) {
        nebulaColors.push(0.2, 0.0, 0.8); // Deep purple
      } else if (colorChoice < 0.6) {
        nebulaColors.push(0.8, 0.0, 0.4); // Magenta
      } else {
        nebulaColors.push(0.0, 0.6, 0.8); // Cyan
      }
      
      sizes.push(Math.random() * 2 + 1);
    }
    
    return {
      positions: new Float32Array(positions),
      colors: new Float32Array(nebulaColors),
      sizes: new Float32Array(sizes)
    };
  }, [nebulaCount]);

  useFrame((state) => {
    if (!audioData || !nebulaRef.current) return;

    const time = state.clock.getElapsedTime();
    const positions = nebulaRef.current.geometry.attributes.position.array;
    const colors = nebulaRef.current.geometry.attributes.color.array;
    
    for (let i = 0; i < nebulaCount; i++) {
      const i3 = i * 3;
      
      // Slow, cosmic drift
      positions[i3] += Math.sin(time * 0.1 + i * 0.01) * 0.01;
      positions[i3 + 1] += Math.cos(time * 0.08 + i * 0.015) * 0.008;
      positions[i3 + 2] += Math.sin(time * 0.12 + i * 0.02) * 0.012;
      
      // Audio-reactive color intensity
      const intensity = 0.3 + audioData.average * 0.7;
      colors[i3] *= intensity;
      colors[i3 + 1] *= intensity;
      colors[i3 + 2] *= intensity;
    }
    
    nebulaRef.current.geometry.attributes.position.needsUpdate = true;
    nebulaRef.current.geometry.attributes.color.needsUpdate = true;
  });

  return (
    <points ref={nebulaRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={nebulaCount}
          array={nebula.positions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          count={nebulaCount}
          array={nebula.colors}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-size"
          count={nebulaCount}
          array={nebula.sizes}
          itemSize={1}
        />
      </bufferGeometry>
      <pointsMaterial
        size={3}
        vertexColors
        transparent
        opacity={0.6}
        sizeAttenuation={true}
        blending={2} // Additive blending for glow effect
      />
    </points>
  );
};

export default NebulaField;